import React from "react";
import moment from "moment";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@material-ui/core";
import { MusicNote as MusicNoteIcon, EventBusy as EventBusyIcon } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { TEAM_COLORS as teamColors } from "CONSTANTS";
import { teamLabel } from "utils/team";

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(4),
  },
  sectionLabel: {
    marginBottom: theme.spacing(1),
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  heroCard: {
    marginBottom: theme.spacing(3),
    borderLeft: "6px solid transparent",
  },
  heroContent: {
    padding: theme.spacing(3),
  },
  heroWhen: {
    fontWeight: 600,
  },
  heroTitle: {
    marginTop: theme.spacing(0.5),
  },
  heroTeam: {
    marginTop: theme.spacing(0.5),
  },
  songChip: {
    marginTop: theme.spacing(2),
  },
  listItem: {
    borderLeft: "4px solid transparent",
    marginBottom: theme.spacing(1),
    borderRadius: 4,
  },
  // Events already over (not ongoing ones - see the "Ongoing" section
  // above, which uses the plain listItem style instead): kept visible
  // for the rest of the day (the backend only excludes previous days),
  // but muted so they read as history rather than something current.
  pastListItem: {
    borderLeft: "4px solid transparent",
    marginBottom: theme.spacing(1),
    borderRadius: 4,
    opacity: 0.6,
  },
  cancelledText: {
    opacity: 0.6,
    textDecoration: "line-through",
  },
  emptyState: {
    textAlign: "center",
    paddingTop: theme.spacing(8),
    color: theme.palette.text.secondary,
  },
  emptyIcon: {
    fontSize: 48,
    marginBottom: theme.spacing(1),
  },
}));

const teamColorFor = (team) => {
  const color = team && teamColors[team.id - 1];
  return color ? color.main : undefined;
};

// Only leaders/admins ever have a draft event in this list at all (the
// backend hides them from everyone else) - the prefix just keeps it
// from reading as a real, already-visible-to-the-team event. Cancelled
// events, unlike drafts, stay visible to everyone - the prefix is what
// tells the team it's off instead of the event just disappearing.
const displayName = (event) => {
  let name = event.name;
  if (event.status === "draft") name = `[Draft] ${name}`;
  if (event.status === "cancelled") name = `[Cancelled] ${name}`;
  return name;
};

const friendlyWhen = (event) =>
  moment(event.time_start).utc().calendar(null, {
    sameElse: "dddd, D MMM [at] HH:mm",
  });

// For events already underway or over earlier today - "Today at
// HH:mm" regardless of whether time_start has technically slipped
// into the past, since that's exactly the point of this section.
const todayWhen = (event) =>
  moment(event.time_start).utc().format("[Today at] HH:mm");

export default ({ events, onSelectEvent }) => {
  const classes = useStyles();

  if (!events || events.length === 0) {
    return (
      <Box className={classes.emptyState}>
        <EventBusyIcon className={classes.emptyIcon} />
        <Typography variant="h6">No upcoming events</Typography>
        <Typography variant="body2">
          Nothing on the calendar yet - check back later.
        </Typography>
      </Box>
    );
  }

  // The backend already limits "upcoming" to today-or-later, so any
  // event that's already started must be from today. Three buckets, not
  // two: still going right now ("Ongoing" - started, hasn't ended),
  // already wrapped up ("Earlier today" - ended), and not started yet
  // (everything else, below). An event mid-way through was previously
  // lumped in with ones already over, reading as history when it's
  // actually the most relevant thing on the list. All three naturally
  // drop off once the day turns over (the backend only excludes
  // previous days).
  const now = moment();
  const isOngoing = (event) =>
    moment(event.time_start).isSameOrBefore(now) && moment(event.time_end).isAfter(now);
  const isOver = (event) => moment(event.time_end).isSameOrBefore(now);
  const ongoingEvents = events.filter(isOngoing);
  const todayEvents = events.filter(isOver);
  const futureEvents = events.filter((event) => !isOngoing(event) && !isOver(event));
  const [nextEvent, ...restEvents] = futureEvents;

  return (
    <Box className={classes.root}>
      {ongoingEvents.length > 0 && (
        <>
          <Typography
            variant="overline"
            color="textSecondary"
            className={classes.sectionLabel}
          >
            Ongoing
          </Typography>
          <List disablePadding>
            {ongoingEvents.map((event) => (
              <Card
                key={event.id}
                variant="outlined"
                className={classes.listItem}
                style={{ borderLeftColor: teamColorFor(event.team) }}
              >
                <CardActionArea onClick={() => onSelectEvent(event)}>
                  <ListItem>
                    <ListItemText
                      primary={
                        event.status === "cancelled" ? (
                          <span className={classes.cancelledText}>
                            {displayName(event)}
                          </span>
                        ) : (
                          displayName(event)
                        )
                      }
                      secondary={
                        todayWhen(event) +
                        (event.team ? ` · ${teamLabel(event.team)}` : "")
                      }
                    />
                  </ListItem>
                </CardActionArea>
              </Card>
            ))}
          </List>
        </>
      )}

      {nextEvent && (
        <>
          <Typography
            variant="overline"
            color="textSecondary"
            className={classes.sectionLabel}
          >
            Next up
          </Typography>
          <Card
            className={classes.heroCard}
            style={{ borderLeftColor: teamColorFor(nextEvent.team) }}
          >
            <CardActionArea onClick={() => onSelectEvent(nextEvent)}>
              <CardContent className={classes.heroContent}>
                <Typography
                  variant="subtitle1"
                  color="secondary"
                  className={classes.heroWhen}
                >
                  {friendlyWhen(nextEvent)}
                </Typography>
                <Typography
                  variant="h5"
                  className={
                    nextEvent.status === "cancelled"
                      ? `${classes.heroTitle} ${classes.cancelledText}`
                      : classes.heroTitle
                  }
                >
                  {displayName(nextEvent)}
                </Typography>
                {nextEvent.team && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    className={classes.heroTeam}
                  >
                    {teamLabel(nextEvent.team)}
                  </Typography>
                )}
                {nextEvent.song_list.length > 0 && (
                  <Chip
                    className={classes.songChip}
                    size="small"
                    icon={<MusicNoteIcon />}
                    label={`${nextEvent.song_list.length} song${
                      nextEvent.song_list.length === 1 ? "" : "s"
                    }`}
                  />
                )}
              </CardContent>
            </CardActionArea>
          </Card>
        </>
      )}

      {restEvents.length > 0 && (
        <>
          <Typography
            variant="overline"
            color="textSecondary"
            className={classes.sectionLabel}
          >
            Later
          </Typography>
          <List disablePadding>
            {restEvents.map((event) => (
              <Card
                key={event.id}
                variant="outlined"
                className={classes.listItem}
                style={{ borderLeftColor: teamColorFor(event.team) }}
              >
                <CardActionArea onClick={() => onSelectEvent(event)}>
                  <ListItem>
                    <ListItemText
                      primary={
                        event.status === "cancelled" ? (
                          <span className={classes.cancelledText}>
                            {displayName(event)}
                          </span>
                        ) : (
                          displayName(event)
                        )
                      }
                      secondary={
                        friendlyWhen(event) +
                        (event.team ? ` · ${teamLabel(event.team)}` : "")
                      }
                    />
                  </ListItem>
                </CardActionArea>
              </Card>
            ))}
          </List>
        </>
      )}

      {todayEvents.length > 0 && (
        <>
          <Typography
            variant="overline"
            color="textSecondary"
            className={classes.sectionLabel}
          >
            Earlier today
          </Typography>
          <List disablePadding>
            {todayEvents.map((event) => (
              <Card
                key={event.id}
                variant="outlined"
                className={classes.pastListItem}
                style={{ borderLeftColor: teamColorFor(event.team) }}
              >
                <CardActionArea onClick={() => onSelectEvent(event)}>
                  <ListItem>
                    <ListItemText
                      primary={
                        event.status === "cancelled" ? (
                          <span className={classes.cancelledText}>
                            {displayName(event)}
                          </span>
                        ) : (
                          displayName(event)
                        )
                      }
                      secondary={
                        todayWhen(event) +
                        (event.team ? ` · ${teamLabel(event.team)}` : "")
                      }
                    />
                  </ListItem>
                </CardActionArea>
              </Card>
            ))}
          </List>
        </>
      )}
    </Box>
  );
};
